import React, { useState, useEffect } from 'react';
import { Button } from './ui/Button';
import { Mail, Lock, User, Phone, MapPin, Calendar, AlertCircle, Briefcase, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';
import { geocodeByAddress } from 'react-google-places-autocomplete';
import { APP_CONFIG, API_URL } from '../config/app';

interface RegisterFormProps {
  onSuccess: () => void;
  onLoginClick?: () => void;
}

// Type pour l'adresse sélectionnée
type AddressOption = {
  value: {
    description: string;
    place_id: string;
  };
  label: string;
};

const RegisterForm: React.FC<RegisterFormProps> = ({ onSuccess, onLoginClick }) => {
  const [formData, setFormData] = useState({
    clientType: 'Particulier',
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    address: '',
    phone: '',
    birthDate: '',
    city: '',
    postalCode: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [addressOption, setAddressOption] = useState<AddressOption | null>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Validation en temps réel des emails
  useEffect(() => {
    if (formData.email && formData.confirmEmail) {
      if (formData.email !== formData.confirmEmail) {
        setEmailError('Les adresses email ne correspondent pas');
      } else {
        setEmailError('');
      }
    } else {
      setEmailError('');
    }
  }, [formData.email, formData.confirmEmail]);

  // Validation en temps réel des mots de passe
  useEffect(() => {
    if (formData.password && formData.confirmPassword) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordError('Les mots de passe ne correspondent pas');
      } else {
        setPasswordError('');
      }
    } else {
      setPasswordError('');
    }
  }, [formData.password, formData.confirmPassword]);

  useEffect(() => {
    if (addressOption) {
      handlePlaceSelect(addressOption);
    }
  }, [addressOption]);

  const handlePlaceSelect = async (option: AddressOption) => {
    try {
      const results = await geocodeByAddress(option.value.description);
      let streetNumber = '';
      let route = '';

      results[0].address_components.forEach((component: any) => {
        const types = component.types;

        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }

        if (types.includes('route')) {
          route = component.long_name;
        }

        if (types.includes('locality')) {
          setFormData(prev => ({ ...prev, city: component.long_name }));
        }

        if (types.includes('postal_code')) {
          setFormData(prev => ({ ...prev, postalCode: component.long_name }));
        }
      });

      // Combine street number and route to form the street address
      const streetAddress = `${streetNumber} ${route}`.trim();
      if (streetAddress) {
        setFormData(prev => ({ ...prev, address: streetAddress }));
      }
    } catch (error) {
      console.error('Error selecting place:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Vérification finale avant soumission
    if (emailError || passwordError) {
      setError('Veuillez corriger les erreurs avant de continuer');
      return;
    }

    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    const addr = formData.address.trim();
    const cityTrim = formData.city.trim();
    const cp = formData.postalCode.trim();
    if (!addr || !cityTrim || !cp) {
      setError(
        'Veuillez choisir une adresse parmi les suggestions pour remplir l’adresse, la ville et le code postal.'
      );
      return;
    }
    if (!/^\d{5}$/.test(cp)) {
      setError('Le code postal doit comporter 5 chiffres.');
      return;
    }

    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Numéro de téléphone invalide (au moins 10 chiffres).');
      return;
    }

    setIsLoading(true);

    try {
      // Préparer les données selon le type de client
      const userData = {
        clientType: formData.clientType,
        firstName: formData.clientType === 'Professionnel' ? formData.companyName : formData.firstName,
        lastName: formData.clientType === 'Professionnel' ? '' : formData.lastName,
        companyName: formData.clientType === 'Professionnel' ? formData.companyName : '',
        email: formData.email,
        password: formData.password,
        address: formData.address,
        phone: formData.phone,
        birthDate: formData.clientType === 'Professionnel' ? null : formData.birthDate,
        city: formData.city,
        postalCode: formData.postalCode,
      };

      // Appel à l'API pour créer un compte
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de l\'inscription');
      }

      // Connexion automatique après inscription réussie
      await login(formData.email, formData.password);
      
      // Notifier le parent du succès
      onSuccess();
      
      // Rediriger vers la page "Mon espace"
      navigate('/mon-espace');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
  };

  const inputBase =
    'bg-canvas text-fg placeholder-gray-400 block w-full pl-9 pr-3 py-1.5 text-sm border border-line-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent';

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-2.5 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
          <span className="text-red-500 text-xs sm:text-sm leading-snug">{error}</span>
        </div>
      )}

      <div className="rounded-lg border border-line-strong/90 bg-canvas/20 p-3 space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-accent-soft flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 text-accent-soft" aria-hidden />
          Compte (e-mail et mot de passe)
        </h3>
        <div className="grid sm:grid-cols-2 gap-2.5 sm:gap-3">
          <div className="space-y-0.5">
            <label htmlFor="email" className="block text-xs font-medium text-fg-muted">
              Email <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`${inputBase} ${emailError ? 'border-red-500' : ''}`}
                placeholder="vous@email.com"
              />
            </div>
            {emailError ? <p className="text-xs text-red-500">{emailError}</p> : null}
          </div>
          <div className="space-y-0.5">
            <label htmlFor="confirmEmail" className="block text-xs font-medium text-fg-muted">
              Confirmer l&apos;e-mail <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
              <input
                id="confirmEmail"
                name="confirmEmail"
                type="email"
                value={formData.confirmEmail}
                onChange={handleChange}
                onPaste={handlePaste}
                required
                className={`${inputBase} ${emailError ? 'border-red-500' : ''}`}
                placeholder="Confirmez"
              />
            </div>
          </div>
          <div className="space-y-0.5">
            <label htmlFor="password" className="block text-xs font-medium text-fg-muted">
              Mot de passe <span className="text-red-500">*</span>
            </label>
            <p className="text-[10px] text-fg-subtle mb-0.5">Au moins 6 caractères.</p>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
                className={`${inputBase} ${passwordError ? 'border-red-500' : ''}`}
                placeholder="••••••••"
              />
            </div>
            {passwordError ? <p className="text-xs text-red-500">{passwordError}</p> : null}
          </div>
          <div className="space-y-0.5">
            <label htmlFor="confirmPassword" className="block text-xs font-medium text-fg-muted">
              Confirmer le mot de passe <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" aria-hidden />
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                onPaste={handlePaste}
                required
                minLength={6}
                autoComplete="new-password"
                className={`${inputBase} ${passwordError ? 'border-red-500' : ''}`}
                placeholder="••••••••"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-line-strong/90 bg-canvas/20 p-3 space-y-2.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-accent-soft flex items-center gap-1.5">
          <User className="h-3.5 w-3.5 text-accent-soft" />
          Profil
        </h3>
        <div className="space-y-0.5">
          <label htmlFor="clientType" className="block text-xs font-medium text-fg-muted">
            Type <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Briefcase className="pointer-events-none absolute left-2.5 top-1/2 z-[1] h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <select
              id="clientType"
              name="clientType"
              value={formData.clientType}
              onChange={handleChange}
              required
              className={`${inputBase} pl-9 appearance-none`}
            >
              <option value="Particulier">Particulier</option>
              <option value="Professionnel">Professionnel</option>
              <option value="Freelancer">Freelancer</option>
            </select>
          </div>
        </div>

        {formData.clientType === 'Professionnel' ? (
          <div className="space-y-0.5">
            <label htmlFor="companyName" className="block text-xs font-medium text-fg-muted">
              Nom de l&apos;entreprise <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Building className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
              <input
                id="companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleChange}
                required
                className={inputBase}
                placeholder="Nom de l'entreprise"
              />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5">
            <div className="space-y-0.5">
              <label htmlFor="firstName" className="block text-xs font-medium text-fg-muted">
                Prénom <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className={inputBase}
                  placeholder="Prénom"
                />
              </div>
            </div>
            <div className="space-y-0.5">
              <label htmlFor="lastName" className="block text-xs font-medium text-fg-muted">
                Nom <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className={inputBase}
                  placeholder="Nom"
                />
              </div>
            </div>
          </div>
        )}

        {formData.clientType !== 'Professionnel' && (
          <div className="space-y-0.5 sm:max-w-[12rem]">
            <label htmlFor="birthDate" className="block text-xs font-medium text-fg-muted">
              Date de naissance <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Calendar className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
              <input
                id="birthDate"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                required={formData.clientType !== 'Professionnel'}
                className={inputBase}
              />
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-line-strong/90 bg-canvas/20 p-3 space-y-2.5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-accent-soft flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-accent-soft" aria-hidden />
          Coordonnées
        </h3>
        <p className="text-[10px] text-fg-subtle -mt-1 mb-1">
          Sélectionnez une suggestion : la ville et le code postal se remplissent automatiquement.
        </p>
        <div className="space-y-0.5">
          <label htmlFor="address" className="block text-xs font-medium text-fg-muted">
            Adresse <span className="text-red-500">*</span>
          </label>
          <GooglePlacesAutocomplete
            apiKey={APP_CONFIG.googleMapsApiKey}
            selectProps={{
              value: addressOption,
              onChange: setAddressOption,
              placeholder: formData.address || 'Rechercher une adresse...',
              styles: {
                control: (provided) => ({
                  ...provided,
                  backgroundColor: 'var(--color-canvas)',
                  color: 'white',
                  border: '1px solid var(--color-line-strong)',
                  borderRadius: '0.5rem',
                  boxShadow: 'none',
                  minHeight: '36px',
                  fontSize: '0.875rem',
                }),
                input: (provided) => ({
                  ...provided,
                  color: 'white',
                }),
                placeholder: (provided) => ({
                  ...provided,
                  color: 'var(--color-fg-muted)',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: 'white',
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? 'var(--color-accent-strong)' : 'var(--color-canvas)',
                  color: 'white',
                  fontSize: '0.875rem',
                }),
                menu: (provided) => ({
                  ...provided,
                  backgroundColor: 'var(--color-canvas)',
                  zIndex: 50,
                }),
              },
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <div className="space-y-0.5">
            <label htmlFor="city" className="block text-xs font-medium text-fg-muted">
              Ville <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
              <input
                id="city"
                name="city"
                type="text"
                value={formData.city}
                onChange={handleChange}
                required
                className={inputBase}
                placeholder="Ville"
                disabled
              />
            </div>
          </div>
          <div className="space-y-0.5">
            <label htmlFor="postalCode" className="block text-xs font-medium text-fg-muted">
              Code postal <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
              <input
                id="postalCode"
                name="postalCode"
                type="text"
                value={formData.postalCode}
                onChange={handleChange}
                required
                className={inputBase}
                placeholder="75001"
                pattern="\d{5}"
                maxLength={5}
                disabled
              />
            </div>
          </div>
        </div>

        <div className="space-y-0.5">
          <label htmlFor="phone" className="block text-xs font-medium text-fg-muted">
            Téléphone <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
            <input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              className={inputBase}
              placeholder="06 XX XX XX XX"
            />
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full py-2.5 text-sm" disabled={isLoading}>
        {isLoading ? 'Inscription…' : 'Créer mon compte'}
      </Button>

      <p className="text-center text-xs text-fg-muted">
        Déjà inscrit ?{' '}
        <button
          type="button"
          onClick={onLoginClick}
          className="text-accent-soft hover:text-accent-soft bg-transparent border-none p-0 font-medium"
        >
          Se connecter
        </button>
      </p>
    </form>
  );
};

export default RegisterForm; 